import { useMemo } from 'react';

import { Box, Container, Typography } from '@mui/material';

import RichHtmlFrame from './RichHtmlFrame';
import { usePromotionalBannerConfig } from '../hooks/useApiQueries';
import { isFullHtmlDocument, sanitizeRichHtml } from '../utils/promotionalBannerHtml';
import { getFranchise } from '../utils';

interface FranchiseBanner {
  franchise_id: string;
  title?: string;
  subtitle?: string;
  content?: string;
  footer_text?: string;
}

interface PromotionalBannerProps {
  franchiseId?: string;
}

const PromotionalBanner = ({ franchiseId }: PromotionalBannerProps) => {
  const { data: bannerData, isLoading } = usePromotionalBannerConfig();

  // Résoudre le contenu par franchise avec fallback sur les valeurs par défaut
  const resolvedConfig = useMemo(() => {
    if (!bannerData?.active || !bannerData?.config) return null;

    const config = bannerData.config;

    // Vérifier si la franchise actuelle est exclue
    if (franchiseId && config.excluded_franchises?.includes(franchiseId)) {
      return null;
    }

    // Chercher une bannière spécifique pour cette franchise
    const franchiseBanners: FranchiseBanner[] = config.franchise_banners || [];
    const currentFranchiseName = franchiseId ? getFranchise(franchiseId) : undefined;
    const franchiseBanner = franchiseId
      ? franchiseBanners.find((fb) => getFranchise(fb.franchise_id) === currentFranchiseName)
      : undefined;

    // Fusionner : champs franchise si définis, sinon fallback sur les valeurs par défaut
    const hasOverride = (value?: string) => value !== undefined && value !== null && value.trim() !== '';

    return {
      ...config,
      title: hasOverride(franchiseBanner?.title) ? franchiseBanner!.title : config.title,
      subtitle: hasOverride(franchiseBanner?.subtitle) ? franchiseBanner!.subtitle : config.subtitle,
      content: hasOverride(franchiseBanner?.content) ? franchiseBanner!.content : config.content,
      footer_text: hasOverride(franchiseBanner?.footer_text) ? franchiseBanner!.footer_text : config.footer_text,
    };
  }, [bannerData, franchiseId]);

  // Si loading ou pas de config résolue, ne rien afficher
  if (isLoading || !resolvedConfig) {
    return null;
  }

  const isContentFullHtml = isFullHtmlDocument(resolvedConfig.content ?? '');
  const contentItems =
    !isContentFullHtml && resolvedConfig.content
      ? resolvedConfig.content.split('|').filter((item: string) => item.trim())
      : [];

  return (
    <Box
      sx={{
        width: '100vw',
        position: 'relative',
        left: '50%',
        right: '50%',
        marginLeft: '-50vw',
        marginRight: '-50vw',
        backgroundColor: resolvedConfig.background_color,
        borderTop: `2px solid ${resolvedConfig.border_color}`,
        borderBottom: `2px solid ${resolvedConfig.border_color}`,
        py: 3,
        my: 4,
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          maxWidth: '1300px',
          px: 3,
        }}
      >
        {resolvedConfig.title && (
          <Typography
            variant="h5"
            sx={{
              color: resolvedConfig.text_color,
              fontWeight: 700,
              textAlign: 'center',
              mb: 1.5,
              fontSize: '1.6rem',
            }}
          >
            {resolvedConfig.title}
          </Typography>
        )}

        {resolvedConfig.subtitle && (
          <Typography
            variant="h6"
            sx={{
              color: resolvedConfig.text_color,
              fontWeight: 600,
              textAlign: 'center',
              mb: 1.5,
              fontSize: '1.28rem',
            }}
          >
            {resolvedConfig.subtitle}
          </Typography>
        )}

        {isContentFullHtml ? (
          <RichHtmlFrame html={sanitizeRichHtml(resolvedConfig.content!)} />
        ) : (
          contentItems.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 3,
                mb: 1.5,
                flexWrap: 'wrap',
              }}
            >
              {contentItems.map((item, index) => (
                <Typography
                  key={index}
                  variant="body1"
                  sx={{
                    color: resolvedConfig.text_color,
                    fontWeight: 600,
                    textAlign: 'center',
                    fontSize: '0.8rem',
                  }}
                  dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(item) }}
                />
              ))}
            </Box>
          )
        )}

        {resolvedConfig.footer_text &&
          (isFullHtmlDocument(resolvedConfig.footer_text) ? (
            <RichHtmlFrame html={sanitizeRichHtml(resolvedConfig.footer_text)} />
          ) : (
            <Box
              sx={{ mt: 2 }}
              dangerouslySetInnerHTML={{
                __html: sanitizeRichHtml(resolvedConfig.footer_text),
              }}
            />
          ))}
      </Container>
    </Box>
  );
};

export default PromotionalBanner;
